import React, { useState, useEffect } from "react";
import { skillsData } from "../../assets/data/courses";
import PasswordDialog from "../PasswordDialog/PasswordDialog";

const ResourceAccordion = ({ onContentChange, currentContent, currentKey }) => {
  const [openSections, setOpenSections] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showPasswordProtection, setShowPasswordProtection] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [authStatus, setAuthStatus] = useState({ mlops: false, ai: false });
  const [protectionSection, setProtectionSection] = useState('mlops');

  // Check auth status on mount
  useEffect(() => {
    setAuthStatus({
      mlops: checkMLOpsAccess(),
      ai: checkAIAccess()
    });
  }, []);

  // Tự động mở nhánh chứa bài học khi currentKey thay đổi
  useEffect(() => {
    if (!currentKey) return;
    let found = false;
    for (const catKey in skillsData) {
      const skill = skillsData[catKey];
      for (const subKey in skill.children) {
        const subSkill = skill.children[subKey];
        if (Array.isArray(subSkill.children)) {
          if (subSkill.children.some((item) => item.key === currentKey)) {
            setOpenSections((prev) => ({
              ...prev,
              [catKey]: true,
              [subKey]: true,
            }));
            found = true;
            break;
          }
        }
      }
      if (found) break;
    }
  }, [currentKey]);

  // Search logic
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }
    const results = [];
    for (const catKey in skillsData) {
      const skill = skillsData[catKey];
      for (const subKey in skill.children) {
        const subSkill = skill.children[subKey];
        if (Array.isArray(subSkill.children)) {
          for (const item of subSkill.children) {
            if (
              item.title &&
              item.title.toLowerCase().includes(searchTerm.toLowerCase())
            ) {
              results.push({
                ...item,
                parentTitle: skill.title,
                subTitle: subSkill.title,
              });
            }
          }
        }
      }
    }
    setSearchResults(results);
  }, [searchTerm]);

  // Check if user has access to MLOps section
  const checkMLOpsAccess = () => {
    const authTime = localStorage.getItem('auth_mlops');
    if (authTime) {
      const currentTime = new Date().getTime();
      const authTimestamp = parseInt(authTime);
      // Authentication expires after 24 hours
      return currentTime - authTimestamp < 24 * 60 * 60 * 1000;
    }
    return false;
  };

  // Check if user has access to AI section
  const checkAIAccess = () => {
    const authTime = localStorage.getItem('auth_ai');
    if (authTime) {
      const currentTime = new Date().getTime();
      const authTimestamp = parseInt(authTime);
      // Authentication expires after 24 hours
      return currentTime - authTimestamp < 24 * 60 * 60 * 1000;
    }
    return false;
  };

  const toggleSection = (section) => {
    // Check if this is MLOps section and user doesn't have access
    if (section === 'MLOps' && !checkMLOpsAccess()) {
      setProtectionSection('mlops');
      setShowPasswordProtection(true);
      setPendingAction(() => () => {
        setOpenSections((prev) => ({
          ...prev,
          [section]: !prev[section],
        }));
      });
      return;
    }

    // Check if this is AI section and user doesn't have access
    if (section === 'AI' && !checkAIAccess()) {
      setProtectionSection('ai');
      setShowPasswordProtection(true);
      setPendingAction(() => () => {
        setOpenSections((prev) => ({
          ...prev,
          [section]: !prev[section],
        }));
      });
      return;
    }

    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleContentChange = (item) => {
    const itemSection = findItemSection(item.key);
    
    // Check if this item belongs to MLOps section
    const isMLOpsContent = item.key && (
      item.key.startsWith('mlops') || 
      item.parentTitle === 'Machine Learning Ops' ||
      itemSection === 'MLOps'
    );

    // Check if this item belongs to AI section
    const isAIContent = item.key && (
      item.key.startsWith('m1') || 
      item.key.startsWith('m2') ||
      item.key.startsWith('m3') ||
      item.key.startsWith('m4') ||
      item.key.startsWith('m5') ||
      item.key.startsWith('m6') ||
      item.key.startsWith('m7') ||
      item.key.startsWith('m8') ||
      item.key.startsWith('m9') ||
      item.parentTitle === 'Artificial Intelligence (AI)' ||
      itemSection === 'AI'
    );

    if (isMLOpsContent && !checkMLOpsAccess()) {
      setProtectionSection('mlops');
      setShowPasswordProtection(true);
      setPendingAction(() => () => onContentChange(item));
      return;
    }

    if (isAIContent && !checkAIAccess()) {
      setProtectionSection('ai');
      setShowPasswordProtection(true);
      setPendingAction(() => () => onContentChange(item));
      return;
    }

    onContentChange(item);
  };

  // Helper function to find which section an item belongs to
  const findItemSection = (itemKey) => {
    for (const catKey in skillsData) {
      const skill = skillsData[catKey];
      for (const subKey in skill.children) {
        const subSkill = skill.children[subKey];
        if (Array.isArray(subSkill.children)) {
          if (subSkill.children.some((item) => item.key === itemKey)) {
            return catKey;
          }
        }
      }
    }
    return null;
  };

  const handlePasswordSuccess = () => {
    setShowPasswordProtection(false);
    // Update auth status for the specific section
    setAuthStatus(prev => ({
      ...prev,
      [protectionSection]: true
    }));
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  };

  const handlePasswordCancel = () => {
    setShowPasswordProtection(false);
    setPendingAction(null);
  };

  const renderChildren = (children) => {
    return children.map((item, index) => (
      <li
        key={item.key || index}
        className={`group cursor-pointer hover:bg-purple-400/5 p-2 rounded-lg transition-all duration-300 ${
          currentContent.key === item.key 
            ? 'bg-purple-400/10 border-l-2 border-purple-400 text-purple-300' 
            : 'text-gray-400 hover:text-purple-300'
        }`}
        onClick={() => handleContentChange(item)}
        title={item.title}
      >
        <span className="font-mono text-xs truncate block">
          <span className="text-purple-400 mr-1">{'>'}</span>
          {item.title}
        </span>
      </li>
    ));
  };

  return (
    <>
      {/* Password Protection Dialog */}
      <PasswordDialog
        isOpen={showPasswordProtection}
        onClose={handlePasswordCancel}
        onSuccess={handlePasswordSuccess}
        title={protectionSection === 'ai' ? "AI - Khu vực bảo mật" : "AI&MLOps - Khu vực bảo mật"}
        section={protectionSection}
      />
      
      <div className="w-[100%] select-none max-sm:w-[100%]">
      {/* Search bar */}
      <div className="pt-3">
        <div className="relative">
          <input
            type="text"
            className="w-full px-3 py-3 bg-black/60 border border-cyan-400/50 rounded-xl text-cyan-100 placeholder-gray-500 font-mono text-xs focus:outline-none focus:border-cyan-400 focus:shadow-neon-cyan transition-all duration-300 backdrop-blur-sm"
            placeholder="Search neural database..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      </div>
      {/* Search results */}
      {searchTerm.trim() ? (
        <div className="mt-3 bg-black/60 border border-cyan-400/30 rounded-xl overflow-hidden backdrop-blur-sm">
          <div className="p-2 bg-black/40 border-b border-cyan-400/20">
            <span className="text-cyan-400 font-mono text-xs font-bold">SEARCH RESULTS</span>
          </div>
          <ul className="divide-y divide-gray-700/50 max-h-60 overflow-y-auto">
            {searchResults.length === 0 && (
              <li className="p-3 text-gray-400 text-xs font-mono text-center">
                <span className="text-cyan-400 mr-2">{'>'}</span>
                NO RESULTS FOUND
              </li>
            )}
            {searchResults.map((item) => (
              <li
                key={item.key}
                className={`group cursor-pointer hover:bg-cyan-400/5 p-2 transition-all duration-300 ${
                  currentContent.key === item.key ? 'bg-cyan-400/10 border-l-2 border-cyan-400' : ''
                }`}
                onClick={() => handleContentChange(item)}
                title={item.title}
              >
                <div className={`font-mono text-xs ${
                  currentContent.key === item.key ? 'text-cyan-300' : 'text-gray-300 group-hover:text-cyan-300'
                } transition-colors duration-300`}>
                  <span className="text-cyan-400 mr-1">{'>'}</span>
                  {item.title}
                </div>
                <div className="text-xs text-gray-500 font-mono mt-1 pl-3">
                  {item.parentTitle} {'>'} {item.subTitle}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        // Accordion view như cũ
        <>
          {Object.keys(skillsData).map((key) => {
            const skill = skillsData[key];
            return (
              <div key={key} className="max-sm:w-[100%]">
                {/* Main Accordion Header */}
                <div
                  className="group flex items-center justify-between bg-black/60 border border-cyan-400/30 p-2 pt-3 pb-3 rounded-xl cursor-pointer mt-3 hover:bg-cyan-400/5 hover:border-cyan-400/50 transition-all duration-300 backdrop-blur-sm"
                  onClick={() => toggleSection(key)}
                >
                  <div className="flex items-center">
                    {/* Icon Placeholder */}
                    {/* <div className={`w-2 h-6 ${skill.color} rounded-full shadow-sm`}></div> */}
                    <span className="font-mono text-xs text-gray-300 group-hover:text-cyan-300 transition-colors duration-300 font-medium">
                      {skill.title}
                    </span>
                    {/* Lock icon for MLOps */}
                    {key === 'MLOps' && !checkMLOpsAccess() && (
                      <svg className="w-4 h-4 text-red-400 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    )}
                    {/* Unlocked icon for authenticated MLOps */}
                    {key === 'MLOps' && checkMLOpsAccess() && (
                      <svg className="w-4 h-4 text-green-400 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                    </svg>
                    )}
                    
                    {/* Lock icon for AI */}
                    {key === 'AI' && !checkAIAccess() && (
                      <svg className="w-4 h-4 text-pink-400 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    )}
                    {/* Unlocked icon for authenticated AI */}
                    {key === 'AI' && checkAIAccess() && (
                      <svg className="w-4 h-4 text-cyan-400 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                      </svg>
                    )}
                  </div>
                  {/* Toggle Icon */}
                  <div
                    className={`transform transition-transform duration-300 ${
                      openSections[key] ? "rotate-180" : ""
                    }`}
                  >
                    <svg
                      className="w-4 h-4 text-gray-400 group-hover:text-cyan-400 transition-colors duration-300"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.22 6.22a.75.75 0 0 1 1.06 0L8 8.94l2.72-2.72a.75.75 0 1 1 1.06 1.06l-3.25 3.25a.75.75 0 0 1-1.06 0L4.22 7.28a.75.75 0 0 1 0-1.06Z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>

                {/* Accordion Content */}
                {openSections[key] && (
                  <div className="mt-2 ml-4 bg-black/40 border border-gray-600/30 rounded-xl backdrop-blur-sm">
                    <ul className="p-2 space-y-1">
                      {Object.keys(skill.children).map((subKey) => {
                        const subSkill = skill.children[subKey];
                        return (
                          <li key={subKey} title={subSkill.title}>
                            <div
                              className="group flex items-center justify-between cursor-pointer hover:bg-gray-600/20 p-2 rounded-lg transition-all duration-300"
                              onClick={() => toggleSection(subKey)}
                            >
                              <span className="text-xs font-mono text-gray-400 group-hover:text-green-400 transition-colors duration-300">
                                <span className="text-green-400 mr-1">{'>'}</span>
                                {subSkill.title}
                              </span>
                              <div
                                className={`transform transition-transform duration-300 ${
                                  openSections[subKey] ? "rotate-180" : ""
                                }`}
                              >
                                <svg
                                  className="w-3 h-3 text-gray-500 group-hover:text-green-400 transition-colors duration-300"
                                  fill="currentColor"
                                  viewBox="0 0 16 16"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M4.22 6.22a.75.75 0 0 1 1.06 0L8 8.94l2.72-2.72a.75.75 0 1 1 1.06 1.06l-3.25 3.25a.75.75 0 0 1-1.06 0L4.22 7.28a.75.75 0 0 1 0-1.06Z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </div>
                            </div>
                            {/* Sub-content */}
                            {openSections[subKey] && (
                              <div className="mt-1 ml-3 bg-black/60 border border-gray-700/50 rounded-lg p-2">
                                <ul className="space-y-1 divide-y divide-gray-700/30">{renderChildren(subSkill.children)}</ul>
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
      </div>
    </>
  );
};

export default ResourceAccordion;
